import React, { useEffect, useState } from 'react';
import { subscribePresence, getPresenceState } from '../../services/presenceService';
import { formatRelativeDate } from '../../utils/relativeTime';

// Re-évaluation locale de la « fraîcheur » : aucun événement Firestore n'arrive après un crash,
// il faut donc recalculer l'état toutes les ~20 s pour passer « hors ligne » quand lastSeen périme.
const REFRESH_INTERVAL_MS = 20 * 1000;

const DOT_CLASSES = {
  online: 'bg-success',
  offline: 'bg-base-300',
  unknown: 'bg-neutral/40 animate-pulse'
};

/**
 * Pastille de présence « en ligne » pour un uid.
 * @param {string} uid - uid de l'utilisateur (client) à observer
 * @param {boolean} withLabel - affiche aussi le libellé texte à côté de la pastille
 * @param {string} className - classes supplémentaires
 */
const PresenceDot = ({ uid, withLabel = false, className = '' }) => {
  const [data, setData] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!uid) {
      setData(null);
      return undefined;
    }
    const unsubscribe = subscribePresence(uid, setData);
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [uid]);

  // Force une re-render périodique pour ré-évaluer la fraîcheur de lastSeen.
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const state = getPresenceState(data);

  let label = 'Statut inconnu';
  if (state === 'online') label = 'En ligne';
  else if (state === 'offline') {
    label = data?.lastSeen
      ? `Hors ligne · vu ${formatRelativeDate(data.lastSeen)}`
      : 'Hors ligne';
  }

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title={label}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${DOT_CLASSES[state]}`} />
      {withLabel && (
        <span className="text-xs text-base-content/70 whitespace-nowrap">{label}</span>
      )}
    </span>
  );
};

export default PresenceDot;